import { useCallback, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { strokeToPath, isSignificantMove, type Point } from './strokeToPath'
import type { WaiverSignaturePaths } from './types'

/**
 * Pointer capture for a drawing surface.
 *
 * Follows the same `setPointerCapture` approach as `useDragSelection` in the
 * mobile booking flow: once the pointer is captured, moves keep arriving even
 * if the finger leaves the element, so a stroke that runs off the edge ends
 * cleanly instead of hanging.
 *
 * Coordinates are taken from `getBoundingClientRect()` and stored against a
 * fixed `viewBox`, so the persisted geometry does not depend on how wide the
 * pad happened to render. A signature drawn on a phone and one drawn on a
 * desktop replay identically.
 */
export function useSignatureCapture(
  value: WaiverSignaturePaths | null,
  onChange: (value: WaiverSignaturePaths | null) => void,
  disabled: boolean,
) {
  const surfaceRef = useRef<SVGSVGElement | null>(null)
  const pointsRef = useRef<Point[]>([])
  const frameRef = useRef<number | null>(null)
  /**
   * Which pointer owns the stroke in progress.
   *
   * A second `pointerdown` mid-stroke — a palm landing on a tablet is enough —
   * used to reset the buffer and discard everything the first pointer had
   * drawn. Tracking identity ignores the intruder instead. Checking
   * `pointsRef.current.length` would look equivalent and is not: a lost
   * `pointerup` would leave the buffer non-empty and the pad permanently dead,
   * whereas a stale id is always cleared by the next `pointerdown` it matches.
   */
  const activePointerRef = useRef<number | null>(null)

  /** The in-progress stroke, mirrored into state so it renders while drawing. */
  const [activePath, setActivePath] = useState<string>('')

  const committed = value?.strokes ?? []

  const toLocalPoint = useCallback((event: ReactPointerEvent<SVGSVGElement>): Point | null => {
    const rect = surfaceRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0 || rect.height === 0) return null

    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    // With nothing committed the surface carries no `viewBox`, so user space is
    // px space and this is the identity. Once a value exists the `<svg>` has its
    // viewBox and `preserveAspectRatio="xMidYMid meet"` — a new stroke must be
    // mapped back through that same fit, the inverse of what the renderer does,
    // or it lands offset from the strokes already drawn. This is what makes a
    // rotation or a soft keyboard mid-signature survive.
    const vb = value?.viewBox
    if (!vb) return { x, y }

    const [minX, minY, vbWidth, vbHeight] = vb
    if (vbWidth <= 0 || vbHeight <= 0) return { x, y }

    const scale = Math.min(rect.width / vbWidth, rect.height / vbHeight)
    const offsetX = (rect.width - vbWidth * scale) / 2
    const offsetY = (rect.height - vbHeight * scale) / 2

    return { x: minX + (x - offsetX) / scale, y: minY + (y - offsetY) / scale }
  }, [value?.viewBox])

  const flush = useCallback(() => {
    frameRef.current = null
    setActivePath(strokeToPath(pointsRef.current))
  }, [])

  const handlePointerDown = useCallback((event: ReactPointerEvent<SVGSVGElement>) => {
    if (disabled || event.button !== 0) return
    if (activePointerRef.current !== null) return
    const point = toLocalPoint(event)
    if (!point) return

    event.currentTarget.setPointerCapture(event.pointerId)
    activePointerRef.current = event.pointerId
    pointsRef.current = [point]
    setActivePath(strokeToPath(pointsRef.current))
  }, [disabled, toLocalPoint])

  const handlePointerMove = useCallback((event: ReactPointerEvent<SVGSVGElement>) => {
    if (disabled || pointsRef.current.length === 0) return
    if (event.pointerId !== activePointerRef.current) return
    const point = toLocalPoint(event)
    if (!point) return

    const previous = pointsRef.current[pointsRef.current.length - 1]
    if (!isSignificantMove(previous, point)) return

    pointsRef.current.push(point)
    // Coalesce to one repaint per frame — a fast pointer fires far more moves
    // than the display can show, and re-rendering per event drops strokes on
    // mid-range phones.
    if (frameRef.current === null) {
      frameRef.current = requestAnimationFrame(flush)
    }
  }, [disabled, flush, toLocalPoint])

  const endStroke = useCallback((event: ReactPointerEvent<SVGSVGElement>) => {
    if (event.pointerId !== activePointerRef.current) return
    activePointerRef.current = null
    if (pointsRef.current.length === 0) return

    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    const path = strokeToPath(pointsRef.current)
    pointsRef.current = []
    setActivePath('')

    if (!path) return

    const rect = surfaceRef.current?.getBoundingClientRect()
    const width = Math.round(rect?.width ?? 0)
    const height = Math.round(rect?.height ?? 0)
    if (width === 0 || height === 0) return

    // Keep the viewBox established by the first stroke. Every committed stroke's
    // coordinates were captured against the surface as it measured at the time,
    // so re-deriving it here would re-frame earlier strokes in a box they were
    // never drawn in — a rotation or a soft keyboard mid-signature is enough to
    // shift everything already on the pad.
    const viewBox = value?.viewBox ?? ([0, 0, width, height] as const)

    onChange({ viewBox: [...viewBox] as [number, number, number, number], strokes: [...committed, path] })
  }, [committed, onChange, value?.viewBox])

  const clear = useCallback(() => {
    activePointerRef.current = null
    pointsRef.current = []
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
    setActivePath('')
    onChange(null)
  }, [onChange])

  return {
    surfaceRef,
    /** Present once a stroke is committed; drives the `<svg>`'s own viewBox. */
    viewBox: value?.viewBox ?? null,
    committed,
    activePath,
    isEmpty: committed.length === 0 && activePath === '',
    handlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: endStroke,
      onPointerCancel: endStroke,
    },
    clear,
  }
}
